import { useState, useEffect, useCallback } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  loadMenuVisibility,
  loadMenuVisibilityFromApi,
  getVisibleMenuItems,
  type MenuItemConfig,
} from "@/config/sidebarMenu";

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  onMobileClose?: () => void;
  isMobile?: boolean;
}

export default function Sidebar({ collapsed, onToggle, onMobileClose, isMobile }: SidebarProps) {
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItemConfig[]>(() =>
    getVisibleMenuItems(loadMenuVisibility())
  );

  const reloadMenu = useCallback(() => {
    setMenuItems(getVisibleMenuItems(loadMenuVisibility()));
  }, []);

  useEffect(() => {
    loadMenuVisibilityFromApi().then((vis) => {
      setMenuItems(getVisibleMenuItems(vis));
    });
  }, []);

  useEffect(() => {
    window.addEventListener("floraiq_menu_changed", reloadMenu);
    return () => window.removeEventListener("floraiq_menu_changed", reloadMenu);
  }, [reloadMenu]);

  const toggleSubmenu = (label: string) => {
    setExpandedMenus((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );
  };

  const handleNavClick = () => {
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  return (
    <aside
      className={`h-full bg-white border-r border-background-200/70 transition-all duration-300 flex flex-col ${
        collapsed && !isMobile ? "w-[68px]" : "w-[260px]"
      }`}
    >
      {/* Logo */}
      <div className="flex items-center h-16 px-4 border-b border-background-200 shrink-0">
        {(!collapsed || isMobile) && (
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary-400 to-primary-600"></div>
              <i className="ri-plant-line text-white text-lg relative z-10"></i>
            </div>
            <div>
              <span className="text-base font-bold text-foreground-900 tracking-tight font-heading">Flora</span>
              <span className="text-base font-bold text-primary-500 tracking-tight font-heading">IQ</span>
            </div>
          </div>
        )}
        {collapsed && !isMobile && (
          <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center mx-auto relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-400 to-primary-600"></div>
            <i className="ri-plant-line text-white text-lg relative z-10"></i>
          </div>
        )}
        {isMobile && (
          <button
            onClick={onMobileClose}
            className="ml-auto md:hidden w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="Fechar menu"
          >
            <i className="ri-close-line text-foreground-600 text-lg"></i>
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {menuItems.map((item) => {
          if (item.children) {
            const isExpanded = expandedMenus.includes(item.label);
            const isAnyChildActive = item.children.some(
              (child) => location.pathname === child.path
            );
            return (
              <div key={item.id}>
                <button
                  onClick={() => toggleSubmenu(item.label)}
                  className={`w-full sidebar-link ${
                    isAnyChildActive ? "sidebar-link-active" : "sidebar-link-inactive"
                  }`}
                  title={collapsed && !isMobile ? item.label : undefined}
                >
                  <span className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className={`${item.icon} text-base`}></i>
                  </span>
                  {(!collapsed || isMobile) && (
                    <>
                      <span className="flex-1 text-left">{item.label}</span>
                      <i
                        className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
                          isExpanded ? "rotate-180" : ""
                        }`}
                      ></i>
                    </>
                  )}
                </button>
                {(!collapsed || isMobile) && isExpanded && (
                  <div className="ml-7 mt-0.5 space-y-0.5 border-l border-background-200/70 pl-3">
                    {item.children.map((child) => (
                      <NavLink
                        key={child.path}
                        to={child.path}
                        onClick={handleNavClick}
                        className={({ isActive }) =>
                          `sidebar-link text-xs py-1.5 ${isActive ? "sidebar-link-active" : "sidebar-link-inactive"}`
                        }
                      >
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          return (
            <NavLink
              key={item.id}
              to={item.path}
              end={item.path === "/"}
              onClick={handleNavClick}
              className={({ isActive }) =>
                `sidebar-link ${isActive ? "sidebar-link-active" : "sidebar-link-inactive"}`
              }
              title={collapsed && !isMobile ? item.label : undefined}
            >
              <span className="w-5 h-5 flex items-center justify-center shrink-0">
                <i className={`${item.icon} text-base`}></i>
              </span>
              {(!collapsed || isMobile) && <span>{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>

      {/* Collapse toggle (desktop only) */}
      {!isMobile && (
        <div className="p-2 border-t border-background-200/70 shrink-0">
          <button
            onClick={onToggle}
            className="w-full sidebar-link sidebar-link-inactive justify-center"
          >
            <i
              className={`ri-arrow-left-s-line text-base transition-transform duration-300 ${
                collapsed ? "rotate-180" : ""
              }`}
            ></i>
            {!collapsed && <span className="text-xs">Recolher menu</span>}
          </button>
        </div>
      )}

      {isMobile && (
        <div className="p-3 border-t border-background-200/70 shrink-0">
          <p className="text-xs text-foreground-400 text-center">FloraIQ v1.0</p>
        </div>
      )}
    </aside>
  );
}
